// Ported from obsidian-plugin/src/components/Message.tsx.
// Obsidian adaptations:
//   - MarkdownRenderer.render → <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]} />
//   - Obsidian "internal-link" anchors that pointed at vault_path → react-router navigate to /sources/:citeKey
//   - new Notice(...) → toast.success / toast.error
//   - react-icons/bs Clipboard/etc → inline Unicode glyphs (no extra dep)
//   - Removed vault "Save as note" / ExportModal (no vault in the browser)
//   - IOC inline highlighting and entity linking are rewritten at the
//     markdown source level by `autolinkMarkdown` and rendered via the
//     `markdownComponents.a` sentinel branches below
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import type {
  SourceDoc,
  PipelineStep,
  PlanningEvent,
  RoutingEvent,
  StepDetail,
  ProfileEvent,
  ScoreThreshold,
  DetectedIOC,
  EntityLink,
} from '../../types';
import { SourceList } from './SourceList';
import { SummaryPopover } from './SummaryPopover';
import EntityContextMenu from '../../components/EntityContextMenu';
import type { EntityDescriptor } from '../../components/EntityContextMenu';
import CopySpecialDialog from '../../components/CopySpecialDialog';
import CopyContextMenu from '../../components/CopyContextMenu';
import { actorDetailPath, familyDetailPath, techniqueDetailPath, iocDetailPath } from '../../lib/paths';
import { FeedbackForm } from '../feedback/FeedbackForm';
import type { CopyScope } from '../../lib/copyFormat';
import { extractTableMarkdown } from '../../lib/copyFormat';
import { autolinkMarkdown } from './autolink';

// Flatten a React node tree to plain text. Used to recover raw code from the
// nested <span> structure that rehype-highlight produces.
function nodeToText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (React.isValidElement(node)) {
    return nodeToText((node.props as { children?: React.ReactNode }).children);
  }
  return '';
}

// Inlined from obsidian-plugin/src/utils/parseThinking.ts.
interface ParsedContent {
  thinking: string;
  body: string;
}
function parseThinking(raw: string): ParsedContent {
  const parts: string[] = [];
  let body = raw
    .replace(/<think>([\s\S]*?)<\/think>/gi, (_, inner) => {
      parts.push(inner.trim());
      return '';
    })
    .trim();
  body = body.replace(/<think>[\s\S]*$/i, '').trim();
  return { thinking: parts.join('\n\n'), body };
}


interface MessageProps {
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceDoc[];
  streaming?: boolean;
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  profileEvent?: ProfileEvent | null;
  scoreThreshold?: ScoreThreshold | null;
  detectedIocs?: DetectedIOC[];
  entityLinks?: EntityLink[];
  turnId?: string;
}

export function Message({
  role,
  content,
  sources,
  streaming,
  detectedIocs,
  entityLinks,
  turnId,
}: MessageProps) {
  const navigate = useNavigate();
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const mdContainerRef = useRef<HTMLDivElement>(null);

  const { thinking, body } = parseThinking(content);
  const sourceMap = useMemo(
    () => new Map((sources || []).map((s) => [s.cite_key, s])),
    [sources],
  );

  // Assign sequential numbers to cite_keys in order of first appearance
  const citeNumberMap = useMemo(() => {
    const map = new Map<string, number>();
    const regex = /\[\[([^\]]+)\]\]/g;
    let match: RegExpExecArray | null;
    let counter = 1;
    while ((match = regex.exec(body)) !== null) {
      if (!map.has(match[1])) map.set(match[1], counter++);
    }
    return map;
  }, [body]);

  // Convert Obsidian [[wiki-links]] to numbered markdown links so
  // react-markdown renders them as <a> elements, triggering sourceMap lookup.
  // Then run the source-level autolinker to rewrite IOC and entity occurrences
  // into sentinel-href markdown links — handled in `markdownComponents.a` below.
  // Source-level rewriting keeps highlighting inside React's render tree and
  // avoids the "Node.insertBefore: Child to insert before is not a child of
  // this node" reconciliation crash that DOM-mutation effects produced.
  const resolvedBody = useMemo(() => {
    const withCites = body.replace(/\[\[([^\]]+)\]\]/g, (_, key) => {
      const num = citeNumberMap.get(key) ?? '?';
      return `[${num}](#cite--${key})`;
    });
    return autolinkMarkdown(withCites, detectedIocs, entityLinks);
  }, [body, citeNumberMap, detectedIocs, entityLinks]);

  useEffect(() => {
    if (sources && sources.length > 0) setSourcesOpen(true);
  }, [sources?.length]);

  const handleCopy = useCallback(() => {
    const text = role === 'user' ? content : body;
    void navigator.clipboard.writeText(text);
    toast.success('Copied');
  }, [body, content, role]);

  // Entity context menu state
  const [entityCtxMenu, setEntityCtxMenu] = useState<{ descriptor: EntityDescriptor; position: { x: number; y: number } } | null>(null);

  // Copy Special dialog state
  const [copySpecial, setCopySpecial] = useState<CopyScope | null>(null);

  // Right-click dropdown state (Copy / Copy Special…)
  const [copyMenu, setCopyMenu] = useState<{ scope: CopyScope; position: { x: number; y: number } } | null>(null);

  const selectionInsideContainer = useCallback((): string => {
    const container = mdContainerRef.current;
    if (!container) return '';
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return '';
    const anchor = sel.anchorNode;
    if (!anchor || !container.contains(anchor)) return '';
    return sel.toString().trim();
  }, []);

  const openCopySpecial = useCallback(() => {
    const text = selectionInsideContainer();
    setCopySpecial(text ? { kind: 'selection', text } : { kind: 'full' });
  }, [selectionInsideContainer]);

  // Inline cite hover state — mirrors SourceList pattern
  const [hoveredCite, setHoveredCite] = useState<{ citeKey: string; el: HTMLElement } | null>(null);
  const citeEnterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const citeGraceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCiteTimers = useCallback(() => {
    if (citeEnterTimeout.current) {
      clearTimeout(citeEnterTimeout.current);
      citeEnterTimeout.current = null;
    }
    if (citeGraceTimeout.current) {
      clearTimeout(citeGraceTimeout.current);
      citeGraceTimeout.current = null;
    }
  }, []);

  const handleCiteEnter = useCallback(
    (citeKey: string, el: HTMLElement) => {
      if (citeKey.startsWith('web:')) return;
      clearCiteTimers();
      citeEnterTimeout.current = setTimeout(() => {
        setHoveredCite({ citeKey, el });
      }, 300);
    },
    [clearCiteTimers],
  );

  const handleCiteLeave = useCallback(() => {
    if (citeEnterTimeout.current) {
      clearTimeout(citeEnterTimeout.current);
      citeEnterTimeout.current = null;
    }
    citeGraceTimeout.current = setTimeout(() => {
      setHoveredCite(null);
    }, 150);
  }, []);

  const handlePopoverEnter = useCallback(() => {
    if (citeGraceTimeout.current) {
      clearTimeout(citeGraceTimeout.current);
      citeGraceTimeout.current = null;
    }
  }, []);

  const handlePopoverLeave = useCallback(() => {
    setHoveredCite(null);
  }, []);

  const handlePopoverClose = useCallback(() => {
    clearCiteTimers();
    setHoveredCite(null);
  }, [clearCiteTimers]);

  useEffect(() => {
    return () => clearCiteTimers();
  }, [clearCiteTimers]);

  // react-markdown `a` component: if the link target matches a known cite_key,
  // turn it into a numbered superscript badge with hover popover.
  const markdownComponents = useMemo(
    () => ({
      // Override `pre` to a pass-through so `code` owns the wrapping chrome.
      pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
      code: ({
        className,
        children,
        ...props
      }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => {
        const isFenced = typeof className === 'string' && /\blanguage-/.test(className);
        if (!isFenced) {
          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        }
        const langMatch = (className || '').match(/language-([\w-]+)/);
        const lang = langMatch ? langMatch[1] : 'text';
        const copy = () => {
          const text = nodeToText(children);
          void navigator.clipboard.writeText(text);
          toast.success('Copied');
        };
        return (
          <div className="malpedia-code-block">
            <div className="malpedia-code-block-header">
              <span className="malpedia-code-lang">{lang}</span>
              <button
                type="button"
                className="malpedia-code-copy"
                onClick={copy}
                title="Copy code"
                aria-label="Copy code"
              >
                ⎘
              </button>
            </div>
            <pre>
              <code className={className} {...props}>
                {children}
              </code>
            </pre>
          </div>
        );
      },
      table: ({ children }: { children?: React.ReactNode }) => {
        const tableRef = useRef<HTMLTableElement>(null);
        const copy = () => {
          const container = mdContainerRef.current;
          const el = tableRef.current;
          let md: string | null = null;
          if (container && el) {
            const tables = Array.from(container.querySelectorAll('table'));
            const idx = tables.indexOf(el);
            if (idx !== -1) md = extractTableMarkdown(body, idx);
          }
          if (!md && el) {
            // Defensive fallback: serialise the live DOM as a pipe table.
            const rows = Array.from(el.querySelectorAll('tr')).map((tr) =>
              Array.from(tr.querySelectorAll('th,td')).map((c) =>
                (c.textContent || '').trim().replace(/\|/g, '\\|'),
              ),
            );
            if (rows.length > 0) {
              const head = rows[0];
              const sep = head.map(() => '---');
              const out = [head, sep, ...rows.slice(1)].map(
                (r) => `| ${r.join(' | ')} |`,
              );
              md = out.join('\n');
            }
          }
          void navigator.clipboard.writeText(md || '');
          toast.success('Copied');
        };
        return (
          <div className="malpedia-table-wrap">
            <button
              type="button"
              className="malpedia-table-copy"
              onClick={copy}
              title="Copy table"
              aria-label="Copy table"
            >
              ⎘
            </button>
            <table ref={tableRef}>{children}</table>
          </div>
        );
      },
      a: ({
        href,
        children,
        ...props
      }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
        const key = href || '';

        // IOC sentinel — emitted by autolinkMarkdown for every DetectedIOC
        // raw-value occurrence in the body. Format:
        //   #mp-ioc:<encodedTypeOriginalCase>:<encodedValue>
        // The original-case type round-trips through the `title` attribute
        // so the right-click context-menu (Message.tsx, onContextMenu)
        // continues to parse `<TYPE>: <value>` and feed `subType=<TYPE>`.
        const iocMatch = key.match(/^#mp-ioc:([^:]+):(.+)$/);
        if (iocMatch) {
          const iocType = decodeURIComponent(iocMatch[1]);
          const iocValue = decodeURIComponent(iocMatch[2]);
          const upper = iocType.toUpperCase();
          const realHref =
            upper === 'THREAT-ACTOR'
              ? actorDetailPath(iocValue)
              : upper === 'MALWARE'
                ? familyDetailPath(iocValue)
                : iocDetailPath(iocValue, iocType.toLowerCase());
          return (
            <a
              href={realHref}
              className={`ioc-highlight ioc-${iocType.toLowerCase()}`}
              title={`${iocType}: ${iocValue}`}
              {...props}
            >
              {children}
            </a>
          );
        }

        // Entity sentinel (detail-page targets — ThreatActor, MalwareFamily,
        // AttackTechnique). Format:
        //   #mp-entity:<encodedLabelOriginalCase>:<encodedName>
        const entityMatch = key.match(/^#mp-entity:([^:]+):(.+)$/);
        if (entityMatch) {
          const label = decodeURIComponent(entityMatch[1]);
          const name = decodeURIComponent(entityMatch[2]);
          const realHref =
            label === 'ThreatActor'
              ? actorDetailPath(name)
              : label === 'MalwareFamily'
                ? familyDetailPath(name)
                : label === 'AttackTechnique'
                  ? techniqueDetailPath(name)
                  : `/graph?seed=${encodeURIComponent(name)}&label=${encodeURIComponent(label)}`;
          const title =
            label === 'ThreatActor'
              ? `View actor details: ${name}`
              : label === 'MalwareFamily'
                ? `View family details: ${name}`
                : label === 'AttackTechnique'
                  ? `View technique details: ${name}`
                  : `Explore ${label}: ${name}`;
          return (
            <a
              href={realHref}
              className={`entity-link entity-link-${label.toLowerCase()} entity-link-detail`}
              title={title}
              {...props}
            >
              {children}
            </a>
          );
        }

        // Entity sentinel (graph-fallback targets — any other label). Format:
        //   #mp-graph:<encodedLabelOriginalCase>:<encodedName>
        // Original-case label is preserved so the right-click handler's
        // LABEL_TO_CATEGORY lookup keeps working.
        const graphMatch = key.match(/^#mp-graph:([^:]+):(.+)$/);
        if (graphMatch) {
          const label = decodeURIComponent(graphMatch[1]);
          const name = decodeURIComponent(graphMatch[2]);
          const realHref = `/graph?seed=${encodeURIComponent(name)}&label=${encodeURIComponent(label)}`;
          return (
            <a
              href={realHref}
              className={`entity-link entity-link-${label.toLowerCase()} entity-link-graph`}
              title={`Explore ${label}: ${name} in graph`}
              {...props}
            >
              {children}
            </a>
          );
        }

        const citeMatch = key.match(/^#cite--(.+)$/);
        if (citeMatch) {
          const citeKey = citeMatch[1];
          const src = sourceMap.get(citeKey);
          const num = citeNumberMap.get(citeKey) ?? '?';
          const targetPath = `/sources/${encodeURIComponent(citeKey)}`;
          return (
            <a
              href={targetPath}
              className="malpedia-cite-badge"
              aria-label={src?.title || citeKey}
              onClick={(e) => {
                e.preventDefault();
                navigate(targetPath);
              }}
              onMouseEnter={(e) => handleCiteEnter(citeKey, e.currentTarget)}
              onMouseLeave={handleCiteLeave}
              {...props}
            >
              {num}
            </a>
          );
        }
        return (
          <a href={href} target="_blank" rel="noreferrer" {...props}>
            {children}
          </a>
        );
      },
    }),
    [sourceMap, citeNumberMap, navigate, handleCiteEnter, handleCiteLeave, body],
  );

  const renderedMarkdown = (
    <div
      ref={mdContainerRef}
      className="markdown-rendered"
      spellCheck={false}
      onClick={(e) => {
        const iocTarget = (e.target as HTMLElement).closest('a.ioc-highlight');
        if (iocTarget && iocTarget instanceof HTMLAnchorElement) {
          e.preventDefault();
          navigate(iocTarget.getAttribute('href')!);
          return;
        }
        const graphTarget = (e.target as HTMLElement).closest('a.entity-link-graph');
        if (graphTarget && graphTarget instanceof HTMLAnchorElement) {
          e.preventDefault();
          navigate(graphTarget.getAttribute('href')!);
        }
      }}
      onContextMenu={(e) => {
        const iocEl = (e.target as HTMLElement).closest('a.ioc-highlight');
        const entityEl = (e.target as HTMLElement).closest('a.entity-link-graph');

        if (!iocEl && !entityEl) {
          // No entity/IOC under cursor → dropdown for assistant turns.
          if (role !== 'assistant') return;
          e.preventDefault();
          const container = mdContainerRef.current;
          const tableEl = (e.target as HTMLElement).closest('table');
          let scope: CopyScope = { kind: 'full' };
          if (container && tableEl && container.contains(tableEl)) {
            const tables = Array.from(container.querySelectorAll('table'));
            const idx = tables.indexOf(tableEl as HTMLTableElement);
            if (idx !== -1) {
              scope = { kind: 'table', index: idx };
            }
          } else {
            const text = selectionInsideContainer();
            if (text) scope = { kind: 'selection', text };
          }
          setCopyMenu({ scope, position: { x: e.clientX, y: e.clientY } });
          return;
        }

        e.preventDefault();
        const pos = { x: e.clientX, y: e.clientY };

        if (iocEl && iocEl instanceof HTMLAnchorElement) {
          const title = iocEl.getAttribute('title') || '';
          const match = title.match(/^([^:]+):\s*(.+)$/);
          const subType = match ? match[1] : undefined;
          const value = match ? match[2] : iocEl.textContent || '';
          const category = /^CVE-/i.test(value) ? 'cve' : 'ioc';
          setEntityCtxMenu({
            descriptor: { value, category, subType },
            position: pos,
          });
          return;
        }

        if (entityEl && entityEl instanceof HTMLAnchorElement) {
          const href = entityEl.getAttribute('href') || '';
          const params = new URLSearchParams(href.split('?')[1] || '');
          const seed = params.get('seed') || entityEl.textContent || '';
          const label = params.get('label') || '';
          const LABEL_TO_CATEGORY: Record<string, string> = {
            MalwareFamily: 'family',
            ThreatActor: 'actor',
            AttackTechnique: 'technique',
            IOC: 'ioc',
            CVE: 'cve',
          };
          setEntityCtxMenu({
            descriptor: {
              value: seed,
              category: LABEL_TO_CATEGORY[label] || 'family',
              neo4jLabel: label || undefined,
            },
            position: pos,
          });
        }
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={markdownComponents}
      >
        {role === 'user' ? content : resolvedBody}
      </ReactMarkdown>
    </div>
  );

  return (
    <div className={`malpedia-message ${role}${streaming ? ' streaming' : ''}`}>
      {role === 'user' ? (
        <>
          <div className="malpedia-message-header">
            <button
              className="malpedia-copy-btn"
              onClick={handleCopy}
              title="Copy message"
              aria-label="Copy message"
            >
              ⎘
            </button>
          </div>
          {renderedMarkdown}
        </>
      ) : (
        <>
          <div className="malpedia-message-header">
            <button
              className="malpedia-copy-btn"
              onClick={handleCopy}
              title="Copy message"
              aria-label="Copy message"
            >
              ⎘
            </button>
            <button
              className="malpedia-copy-btn"
              onClick={openCopySpecial}
              title="Copy with options"
              aria-label="Copy with options"
            >
              ⎘…
            </button>
          </div>
          {renderedMarkdown}
          {hoveredCite && (
            <SummaryPopover
              targetEl={hoveredCite.el}
              citeKey={hoveredCite.citeKey}
              title={sourceMap.get(hoveredCite.citeKey)?.title}
              onClose={handlePopoverClose}
              onMouseEnter={handlePopoverEnter}
              onMouseLeave={handlePopoverLeave}
            />
          )}
          {entityCtxMenu && (
            <EntityContextMenu
              descriptor={entityCtxMenu.descriptor}
              position={entityCtxMenu.position}
              onClose={() => setEntityCtxMenu(null)}
            />
          )}
          {copyMenu && (
            <CopyContextMenu
              scope={copyMenu.scope}
              body={body}
              sources={sources}
              position={copyMenu.position}
              onOpenSpecial={() => {
                setCopySpecial(copyMenu.scope);
                setCopyMenu(null);
              }}
              onClose={() => setCopyMenu(null)}
            />
          )}
          {copySpecial && (
            <CopySpecialDialog
              body={body}
              sources={sources}
              initialScope={copySpecial}
              onClose={() => setCopySpecial(null)}
            />
          )}
          {streaming && content && <span className="malpedia-streaming-cursor" />}

          {(thinking || (sources && sources.length > 0)) && (
            <div className="malpedia-message-footer">
              {thinking && (
                <button
                  className={`malpedia-section-toggle${thinkingOpen ? ' open' : ''}`}
                  onClick={() => setThinkingOpen((o) => !o)}
                >
                  <span>💡</span>
                  <span>Reasoning</span>
                  <span className="malpedia-section-toggle-chevron">▾</span>
                </button>
              )}
              {sources && sources.length > 0 && (
                <button
                  className={`malpedia-section-toggle${sourcesOpen ? ' open' : ''}`}
                  onClick={() => setSourcesOpen((o) => !o)}
                >
                  <span>📑</span>
                  <span>{sources.length} sources</span>
                  <span className="malpedia-section-toggle-chevron">▾</span>
                </button>
              )}
            </div>
          )}
          {thinking && (
            <div className={`malpedia-collapsible${thinkingOpen ? ' open' : ''}`}>
              <div className="malpedia-collapsible-inner">
                <div className="malpedia-thinking-content">{thinking}</div>
              </div>
            </div>
          )}
          {sources && sources.length > 0 && (
            <div className={`malpedia-collapsible${sourcesOpen ? ' open' : ''}`}>
              <div className="malpedia-collapsible-inner">
                <SourceList sources={sources} />
              </div>
            </div>
          )}
          {!streaming && turnId && (
            <div className="malpedia-message-feedback">
              <FeedbackForm turnId={turnId} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
